import { useState } from 'react';
import { CheckCircleIcon, UserIcon, DocumentTextIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { verificationApi } from '../../services/api';
import { useVerificationStore } from '../../stores/verificationStore';
import { Alert } from '../shared/Alert';
import { LoadingSpinner } from '../shared/LoadingSpinner';

interface ReviewStepProps {
  onNext: () => void;
  onBack: () => void;
}

export function ReviewStep({ onNext, onBack }: ReviewStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { requestId, personalInfo, setStep } = useVerificationStore();

  const handleSubmit = async () => {
    if (!requestId) return;

    setError(null);
    setIsSubmitting(true);

    try {
      await verificationApi.submit(requestId);
      setStep('payment');
      onNext();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!personalInfo) {
    return <Alert type="error" message="Personal information is missing" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Review Your Application</h2>
        <p className="text-sm text-gray-600 mt-1">
          Please review your information before proceeding to payment.
        </p>
      </div>

      {error && <Alert type="error" message={error} />}

      <div className="space-y-4">
        {/* Personal Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <UserIcon className="h-5 w-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">Personal Information</h3>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div>
              <dt className="text-gray-500">Full Name</dt>
              <dd className="font-medium text-gray-900">
                {personalInfo.firstName} {personalInfo.lastName}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">ID Number</dt>
              <dd className="font-medium text-gray-900">{personalInfo.idNumber}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Date of Birth</dt>
              <dd className="font-medium text-gray-900">
                {new Date(personalInfo.dateOfBirth).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Address */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <MapPinIcon className="h-5 w-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">Address</h3>
          </div>
          <p className="text-sm text-gray-900">
            {personalInfo.streetAddress}
            <br />
            {personalInfo.city}, {personalInfo.province} {personalInfo.postalCode}
          </p>
        </div>

        {/* Documents */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <DocumentTextIcon className="h-5 w-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">Documents</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span>ID Document uploaded</span>
            </div>
            <div className="flex items-center text-sm">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span>Selfie captured</span>
            </div>
            <div className="flex items-center text-sm">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span>Phone number verified</span>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-medium text-gray-900">Verification Fee</h3>
              <p className="text-sm text-gray-600">One-time payment</p>
            </div>
            <div className="text-2xl font-bold text-primary-600">R150.00</div>
          </div>
        </div>

        <Alert
          type="info"
          message="By proceeding to payment, you confirm that all information provided is accurate and authorize us to query the Department of Home Affairs on your behalf."
        />
      </div>

      <div className="flex justify-between pt-4">
        <button type="button" onClick={onBack} className="btn-secondary">
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? <LoadingSpinner size="sm" /> : 'Proceed to Payment'}
        </button>
      </div>
    </div>
  );
}
